import { useRef, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import EventDisplay from "../components/CommonComponents/EventDisplay";
import TimePanel from "../components/first_screen/TimePanel";
import PrayerGrid from "../components/first_screen/PrayerGrid";
import SunriseSunsetCard from "../components/first_screen/SunriseSunsetCard";
import FooterStrip from "../components/first_screen/FooterStrip";
import AnnouncementDisplay from "../components/CommonComponents/AnnouncementDisplay";
import ImageDisplay from "../components/CommonComponents/ImageDisplay";

const FirstScreen = ({
  colorSettings = {},
  // Data props (Ready to use)
  prayerData, 
  timezone,
  currentTime,
  // Scheduler props (Ready to use)
  moduleData: currentOuter, 
  innerModule: currentInner, // This is already hydrated by Resolver!
  announcements // This is already fetched by Resolver!
}) => {
  const {
    headerBackgroundColor = "#ffffff",
    footerBackgroundColor = "#e5eaff",
    footerTextColor = "#3d3dff",
  } = colorSettings;

  const [nextEvent, setNextEvent] = useState(null);
  const nodeRef = useRef(null);

  const isOuterFullScreen = currentOuter && !currentOuter.parent_Id;

  // Helper to extract data from the hydrated module
  const innerModuleName = currentInner?.source_Module_Name?.toLowerCase();
  const innerData = currentInner?.data;

  //console.log(prayerData);

  // --- Render Outer Full Screen ---
  const renderOuterModule = () => {
      // Since Resolver handles the main structure, this is likely redundant 
      // unless you have specific logic for FullScreen mode *inside* FirstScreen structure.
      // Assuming FirstScreen structure supports FullScreen logic:
      return (
        <>
            <div className="flex flex-wrap items-center border-b-[0.47svh] border-gray-300" style={{padding:"0.1rem 0.1rem 0rem 0.1rem"}}>
            <div className="w-full md:w-2/3 pr-[0.2svh] pb-[0.2svh] md:border-r-[0.47svh] md:border-gray-300 sm:min-h-[70svh]">
              <SwitchTransition mode="out-in">
                <CSSTransition
                  key={(currentInner?.source_Module_Name || "") + (currentInner?.id || "")}
                  timeout={500}
                  classNames="inner-module-fade"
                  nodeRef={nodeRef}
                >
                  <div ref={nodeRef}>
                    {(() => {
                      if (innerModuleName?.includes("event") && innerData) {
                        return <EventDisplay event={innerData[0]} />;
                      }
                      if (innerModuleName?.includes("images") && innerData?.length > 0) {
                        return <ImageDisplay image={innerData[0]} />;
                      }
                      if (innerModuleName === "announcements" && innerData?.length > 0) {
                        return <AnnouncementDisplay announcement={innerData[0]} />;
                      }
                      return null;
                    })()}
                  </div>
                </CSSTransition>
              </SwitchTransition>
            </div>

            <div className="w-full md:w-1/3 px-[1svh] text-center pt-[1svh]">
              <TimePanel
                colorSettings={colorSettings}
                prayerData={prayerData}
                timezone={timezone}
                currentTime={currentTime}
                onNextEventChange={setNextEvent}
              />
            </div>
          </div>
          
          {/* Bottom Grid + Footer */}
          <div style={{padding:"0rem 0.1rem 0.1rem 0.1rem"}}>
            <div className="flex flex-wrap pt-[0.5svh]">
              <div
                className="w-full md:w-2/3"
                style={{ backgroundColor: "rgb(9, 109, 109)", borderRight: "0.6svh solid white" }}
              >
                <PrayerGrid
                  colorSettings={colorSettings}
                  prayerData={prayerData}
                  nextEvent={nextEvent}
                />
              </div>
              <div
                className="w-full md:w-1/3"
                style={{ backgroundColor: "rgb(9, 109, 109)", borderLeft: "0.3svh solid white" }}
              >
                <SunriseSunsetCard colorSettings={colorSettings} prayerData={prayerData} />
              </div>
            </div>

            <div
              className="w-full"
              style={{ backgroundColor: footerBackgroundColor, color: footerTextColor }}
            >
              <FooterStrip colorSettings={colorSettings} announcements={announcements} logo_Url={prayerData.logo_Url} />
            </div>
          </div>
        </>
      );
  };

  return (
    <>
      {isOuterFullScreen ? (
        renderOuterModule()
      ) : (
        <>
          {/* STANDARD LAYOUT */}
          <div className="flex flex-wrap sm:flex-nowrap w-full gap-2 h-[65vh] xl:h-[70vh]">
            {/* Inner Module Display */}
            <div className="w-full sm:w-[70%] h-full">
              <div
                className="w-full h-full shadow-md overflow-hidden"
                style={{ backgroundColor: headerBackgroundColor }}
              >
                 {(() => {
                      if (innerModuleName?.includes("event") && innerData) {
                        return <EventDisplay event={innerData[0]} />;
                      }
                      if (innerModuleName?.includes("images") && innerData?.length > 0) {
                        return <ImageDisplay image={innerData[0]} />;
                      }
                      if (innerModuleName === "announcements" && innerData?.length > 0) {
                        return <AnnouncementDisplay announcement={innerData[0]} />;
                      }
                      return null;
                })()}
              </div>
            </div>

            {/* Time Panel */}
            <div className="w-full sm:w-[30%] h-full">
              <div
                className="w-full h-full shadow-md overflow-hidden"
                style={{ backgroundColor: headerBackgroundColor }}
              >
                <TimePanel
                  colorSettings={colorSettings}
                  prayerData={prayerData}
                  timezone={timezone}
                  currentTime={currentTime}
                  onNextEventChange={setNextEvent}
                />
              </div>
            </div>
          </div>

          {/* Prayer Grid */}
          <div className="flex flex-wrap sm:flex-nowrap w-full gap-0 h-[20vh] xl:h-[22vh] mt-0 mb-0 pb-0 pt-1">
            <div className="w-full sm:w-[70%] h-full">
              <PrayerGrid
                colorSettings={colorSettings}
                prayerData={prayerData}
                nextEvent={nextEvent}
              />
            </div>
            <div className="w-full sm:w-[30%] h-full">
              <SunriseSunsetCard colorSettings={colorSettings} prayerData={prayerData} />
            </div>
          </div>

          {/* Footer Announcements */}
          <div className="w-full flex-1 overflow-auto pt-0">
            <div
              className="w-full shadow-sm"
              style={{ backgroundColor: footerBackgroundColor, color: footerTextColor }}
            >
              <FooterStrip colorSettings={colorSettings} announcements={announcements} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FirstScreen;